import { Box, SxProps, Theme } from '@mui/material'
import clsx from 'clsx'
import { LucideIcon, LucideProps } from 'lucide-react'
import { ComponentType, forwardRef, SVGProps } from 'react'

// Size mapping - matches MUI's fontSize values for SvgIcon
const SIZE_MAP = {
    sm: { fontSize: '1rem', className: 'MuiSvgIcon-fontSizeSmall' }, // 16px
    md: { fontSize: '1.5rem', className: 'MuiSvgIcon-fontSizeMedium' }, // 24px
    lg: { fontSize: '2.1875rem', className: 'MuiSvgIcon-fontSizeLarge' }, // 35px
} as const

type IconSize = keyof typeof SIZE_MAP

// Custom SVG component type (for non-Lucide icons)
type CustomSvgIcon = ComponentType<SVGProps<SVGSVGElement>>

// Base props shared by both variants
interface BaseC1IconProps {
    /** Size preset controlling the rendered icon dimensions.
     * - `sm` — 16px (1rem). Default. Use for inline text, table cells, and compact UI.
     * - `md` — 24px (1.5rem). Use for standalone icons or primary actions.
     * - `lg` — 35px (2.1875rem). Use for empty states, hero sections, or large callouts.
     */
    size?: IconSize
    /** MUI system styles merged onto the root `<span>` wrapper. Use to override color, margin, or sizing. */
    sx?: SxProps<Theme>
    /** Additional CSS class name applied to the root wrapper. */
    className?: string
    /** Optional overlay icon rendered absolutely on top of the main icon. Use for "off" or "disabled" variants (e.g., pass `Slash` to create a struck-through icon). */
    overlayIcon?: LucideIcon
    /** MUI system styles applied to the overlay icon container. */
    overlaySx?: SxProps<Theme>
}

// Props for Lucide icons (default)
interface LucideC1IconProps extends BaseC1IconProps, Omit<LucideProps, 'size' | 'color'> {
    /** The Lucide icon component to render (e.g., `Plus`, `Check`, `Pencil`). When `custom` is true, pass a custom SVG component instead. */
    icon: LucideIcon
    /** Set to `true` when passing a non-Lucide custom SVG component as the `icon` prop. Omit or set to `false` for Lucide icons. */
    custom?: false
}

// Props for custom SVG icons
interface CustomC1IconProps extends BaseC1IconProps {
    /** A custom SVG component (non-Lucide) to render. Requires `custom={true}`. */
    icon: CustomSvgIcon
    /** Must be `true` when using a custom SVG component instead of a Lucide icon. Changes rendering to pass `width`/`height` instead of `strokeWidth`. */
    custom: true
}

// Union type - TypeScript will enforce correct usage
export type C1IconProps = LucideC1IconProps | CustomC1IconProps

/**
 * Unified icon component that bridges Lucide icons (and custom SVG components)
 * with the MUI theme system.
 *
 * Renders a Lucide icon (or custom SVG) inside a `<span>` that carries MUI's
 * `MuiSvgIcon-root` classes, so parent MUI components like Button, IconButton,
 * and ListItemIcon can control icon size and color through their existing CSS
 * rules — no extra styling needed at the call site.
 *
 * ## Sizes
 * - `sm` (default) — 16px. Inline text, table cells, chips, compact UI.
 * - `md` — 24px. Standalone icons, primary action indicators.
 * - `lg` — 35px. Empty states, hero sections, large callouts.
 *
 * Each size maps to the corresponding MUI `fontSize` class
 * (`MuiSvgIcon-fontSizeSmall`, `Medium`, `Large`), so the icon inherits
 * size from the parent when used inside MUI components.
 *
 * ## Color
 * Inherits `currentColor` by default. Override with `sx={{ color: 'error.main' }}`
 * or let the parent component control it (e.g., a `C1Button` sets the color
 * automatically).
 *
 * ## Overlay icons
 * Pass an `overlayIcon` (e.g., `Slash`) to render a second icon absolutely
 * positioned on top. This is the standard pattern for "off" or "disabled"
 * icon variants (e.g., `<C1Icon icon={Code} overlayIcon={Slash} />` for
 * "code off").
 *
 * ## Custom SVG icons
 * For non-Lucide SVG components (identity provider logos, custom illustrations),
 * pass `custom={true}`. This switches rendering from Lucide's `strokeWidth`
 * API to plain `width`/`height` props on the SVG.
 *
 * ```tsx
 * // Lucide icon (default)
 * <C1Icon icon={Plus} />
 * <C1Icon icon={Check} size="lg" sx={{ color: 'success.main' }} />
 *
 * // Custom SVG icon
 * <C1Icon icon={OktaLogo} custom />
 *
 * // Icon with overlay ("off" variant)
 * <C1Icon icon={Code} overlayIcon={Slash} />
 * ```
 *
 * @designSystem
 * @category atoms
 * @designSystemProps icon, custom, size, sx, className, overlayIcon, overlaySx
 * @relatedComponent C1IconButton | Use when you need a clickable icon button wrapping C1Icon
 * @externalReference Lucide React | https://lucide.dev/guide/packages/lucide-react
 */
export const C1Icon = forwardRef<HTMLSpanElement, C1IconProps>(({
    icon: Icon,
    size = 'sm',
    sx,
    className,
    custom,
    overlayIcon: OverlayIcon,
    overlaySx,
    ...props
}, ref) => (
    <Box
        ref={ref}
        component="span"
        className={clsx('MuiSvgIcon-root', SIZE_MAP[size].className, className)}
        sx={{
            // Match MUI SvgIcon base styles
            display: 'inline-block',
            fill: 'currentColor',
            width: '1em',
            height: '1em',
            fontSize: SIZE_MAP[size].fontSize,
            flexShrink: 0,
            userSelect: 'none',
            // Position relative when overlay is present
            position: OverlayIcon ? 'relative' : undefined,
            // Make the svg child fill this container
            '& > svg': {
                display: 'block',
                width: '100%',
                height: '100%',
            },
            ...sx,
        }}
    >
        {custom
            ? <Icon width="100%" height="100%" />
            : <Icon strokeWidth={1.5} {...props as Omit<LucideProps, 'size' | 'color'>} />}
        {OverlayIcon && (
            <Box
                component="span"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& > svg': {
                        width: '100%',
                        height: '100%',
                    },
                    ...overlaySx,
                }}
            >
                <OverlayIcon />
            </Box>
        )}
    </Box>
))

C1Icon.displayName = 'C1Icon'

// Re-export for convenience
export type { LucideIcon, CustomSvgIcon }
